    /*
     * File Path: client/src/api/deviceService.js
     * Description: Service functions for managing devices (CRUD, credentials).
     * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
     * BDAO-SIS Project
     * Version: 1.1.0 // Increment version
     * CreateDate: 2025-05-01
     * UpdateDate: 2025-05-01 // Date of adding fetchGroupedDevices
     */

    import { apiClient, handleApiError } from './apiClient';

    // ... other functions (addDevice, getDeviceDetails, etc.) ...
    // Keep all the other functions like addDevice, getDeviceDetails, etc. here

    /**
     * Adds a new device to a property.
     * @param {object} payload - Device details (e.g., { property_id, name, type, mac_address?, ... }).
     * @returns {Promise<object>} The newly created device object, potentially including mqtt_api_key (handle with care).
     * @throws {Error} Custom error on failure.
     */
    export const addDevice = async (payload) => {
        try {
            // Endpoint: POST /api/auth/devices
            const response = await apiClient.post('/auth/devices', payload);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Fetches details for a specific device by its ID. Excludes sensitive keys.
     * @param {string} deviceId - The UUID of the device.
     * @returns {Promise<object>} Device details object.
     * @throws {Error} Custom error on failure.
     */
    export const getDeviceDetails = async (deviceId) => {
        try {
            // Endpoint: GET /api/auth/devices/:deviceId
            const response = await apiClient.get(`/auth/devices/${deviceId}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Fetches device credentials (device_id, mqtt_api_key) based on user, property, and MAC address.
     * Intended for device provisioning. Note: Backend route might not require standard session auth.
     * @param {number|string} userId - The owner's user ID.
     * @param {number|string} propertyId - The property ID the device belongs to.
     * @param {string} macAddress - The MAC address of the device.
     * @returns {Promise<object>} Object containing { device_id, mqtt_api_key }.
     * @throws {Error} Custom error on failure (e.g., 404 if not found).
     */
    export const fetchDeviceCredentials = async (userId, propertyId, macAddress) => {
        try {
            const payload = { userId, propertyId, macAddress };
            // Endpoint: POST /api/auth/devices/credentials
            const response = await apiClient.post('/auth/devices/credentials', payload);
            return response.data;
        } catch (error) {
            console.error("Error fetching device credentials:", error.response?.data || error.message);
            handleApiError(error);
        }
    };

    /**
     * Updates an existing device owned by the logged-in user.
     * @param {string} deviceId - The UUID of the device to update.
     * @param {object} payload - Device details to update (e.g., { name?, type?, enabled?, state?, value? ... }).
     * @returns {Promise<object>} The updated device object.
     * @throws {Error} Custom error on failure.
     */
    export const updateDevice = async (deviceId, payload) => {
        try {
            // Endpoint: PUT /api/auth/devices/:deviceId
            const response = await apiClient.put(`/auth/devices/${deviceId}`, payload);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Updates only the model and manufacturer info for a device owned by the logged-in user.
     * Requires MAC address for verification on the backend for this specific route.
     * @param {string} deviceId - The UUID of the device to update.
     * @param {object} payload - { model?, manufacturer?, mac_address }.
     * @returns {Promise<object>} The updated device object.
     * @throws {Error} Custom error on failure.
     */
    export const updateDeviceModelInfo = async (deviceId, payload) => {
        try {
            // Endpoint: PUT /api/auth/devices/:deviceId/model-info
            const response = await apiClient.put(`/auth/devices/${deviceId}/model-info`, payload);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Deletes a device owned by the logged-in user.
     * @param {string} deviceId - The UUID of the device to delete.
     * @returns {Promise<object>} Backend response on success (often empty).
     * @throws {Error} Custom error on failure.
     */
    export const deleteDevice = async (deviceId) => {
        try {
            // Endpoint: DELETE /api/auth/devices/:deviceId
            const response = await apiClient.delete(`/auth/devices/${deviceId}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    // --- Operations by MAC Address ---

    /**
     * Fetches details for a specific device by its MAC Address. Requires user authentication.
     * @param {string} macAddress - The MAC address of the device.
     * @returns {Promise<object>} Device details object.
     * @throws {Error} Custom error on failure.
     */
    export const getDeviceDetailsByMac = async (macAddress) => {
        try {
            const encodedMac = encodeURIComponent(macAddress);
            // Endpoint: GET /api/auth/devices/mac/:macAddress
            const response = await apiClient.get(`/auth/devices/mac/${encodedMac}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Deletes a device owned by the logged-in user, identified by its MAC Address.
     * @param {string} macAddress - The MAC address of the device to delete.
     * @returns {Promise<object>} Backend response on success (often empty).
     * @throws {Error} Custom error on failure.
     */
    export const deleteDeviceByMac = async (macAddress) => {
        try {
            const encodedMac = encodeURIComponent(macAddress);
            // Endpoint: DELETE /api/auth/devices/mac/:macAddress
            const response = await apiClient.delete(`/auth/devices/mac/${encodedMac}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Fetches MQTT credentials (device_id, mqtt_api_key) for a device by its MAC address.
     * Intended for device provisioning. Backend route likely has no standard session auth.
     * @param {string} macAddress - The MAC address of the device.
     * @returns {Promise<object>} Object { device_id, mqtt_api_key }.
     * @throws {Error} Custom error on failure (e.g., 404 if not found/enabled).
     */
    export const fetchMqttCredentialsByMac = async (macAddress) => {
        try {
            const encodedMac = encodeURIComponent(macAddress);
            // Endpoint: GET /api/auth/devices/mqtt-credentials/:macAddress
            const response = await apiClient.get(`/auth/devices/mqtt-credentials/${encodedMac}`);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    /**
     * Updates the model and manufacturer info for a device identified by its MAC address.
     * Intended primarily for the device itself to call. Backend route likely has no standard session auth.
     * @param {string} macAddress - The MAC address of the device.
     * @param {object} payload - { model?, manufacturer? }.
     * @returns {Promise<object>} Backend response { message, data: { device_id, model, manufacturer } }.
     * @throws {Error} Custom error on failure.
     */
    export const updateDeviceModelInfoByMac = async (macAddress, payload) => {
        try {
            const encodedMac = encodeURIComponent(macAddress);
            // Endpoint: PUT /api/auth/devices/mac/:macAddress/model-info
            const response = await apiClient.put(`/auth/devices/mac/${encodedMac}/model-info`, payload);
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };

    // --- Fetch Devices for Grouped Display ---
    /**
     * Fetches all devices associated with the user (owned or shared properties).
     * Includes property name for grouping.
     * @returns {Promise<Array<object>>} Flat array of device objects, each including property_id and property_name.
     * @throws {Error} Custom error on failure.
     */
    export const fetchGroupedDevices = async () => { // <<< MAKE SURE THIS FUNCTION AND EXPORT EXIST
        try {
            // Endpoint: GET /api/auth/devices/grouped
            const response = await apiClient.get('/auth/devices/grouped');
            return response.data;
        } catch (error) {
            handleApiError(error);
        }
    };
    